import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE, EQ } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { isEmpty } from "../../utils/utils";
import { gstValues } from "../../utils/constant";
import NumberInput from "../../framework/NumberInput";

const ServiceItemMasterFilter = ({
  reportRef,
  onFilterShow,
  applyFilterOnMount = true,
  applyFilters = () => {},
  refreshData = () => {},
}) => {
  const [serviceId, setServiceId] = useState("");
  const [serviceItem, setServiceItem] = useState("");
  const [gst, setGst] = useState(null);
  const [limit, setLimit] = useState(100);

  const [orderBy] = useState([{ fieldName: "updatedTime", orderType: "desc" }]);

  const apr_filter_service_id_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(gst)) {
        filters.push(createFilterParam("gst", gst.id, "number", EQ));
      }
      if (!isEmpty(serviceItem)) {
        filters.push(
          createFilterParam("productName", serviceItem, "string", LIKE)
        );
      }
      if (!isEmpty(serviceId)) {
        filters.push(createFilterParam("productId", serviceId, "number", LIKE));
      }
      if (!isEmpty(limit)) {     
        result.limit = limit;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      if (isFilterChanged) {
        result.filters = filters;
      }
      if (orderBy) {
        result.orderBy = orderBy;
      }
      applyFilters(result,{
        serviceId,
        serviceItem,
        gst,
        limit,
      });
      reportRef.current?.applyFilters(result);
    },
    [orderBy, serviceId, serviceItem, gst, reportRef,limit,applyFilters]
  );
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <TextInput
            label="Service Id"
            id="apr_filter_service_id"
            ref={apr_filter_service_id_ref}
            onFocus={() => setCurrentComponent("apr_filter_service_id")}
            onChange={(e) => {
              setServiceId(e.target.value);
            }}
            value={serviceId}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_service_id",
                left: "apr_filter_service_id",
                down: "apr_filter_service_item",
                right: "apr_filter_service_item",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_service_item"
            label="Service Item"
            onFocus={() => setCurrentComponent("apr_filter_service_item")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_service_id",
                down: "add_hsn_gst",
                right: "add_hsn_gst",
                left: "apr_filter_service_id",
              }),
            }}
            value={serviceItem}
            onChange={(e) => {
              setServiceItem(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="add_hsn_gst"
            name="hsnGst"
            disableClearable={false}
            label="GST %"
            onFocus={() => setCurrentComponent("add_hsn_gst")}
            onChange={(event, option) => {
              setGst(option);
            }}
            getOptionLabel={(option) => {
              return option.label ||option;
            }}
            isOptionEqualToValue={(option, value) => option=== value}
            options={gstValues}
            markers={JSON.stringify({
              up: "apr_filter_service_item",
              down: "apr_filter_limit",
              left: "apr_filter_service_item",
              right: "apr_filter_limit",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            label="Limit"
            id="apr_filter_limit"
            onFocus={() => setCurrentComponent("apr_filter_limit")}
            value={limit}
            onValueChange={(values, e) => {
              setLimit(values.floatValue);
            }}
            isAllowed={(values) => {
              return values?.formattedValue <= 999999;
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_hsn_gst",
                left: "add_hsn_gst",
                down: "apr_apply_filter_button",
                right: "apr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="apr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "apr_filter_limit",
              left: "apr_filter_limit",
              right: "apr_apply_filter_button",
              down: "apr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  useEffect(() => {
    if(refreshData) {
      applyFilterHandler(true);
    }
  },[refreshData,applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      apr_filter_service_id_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);
  
  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default ServiceItemMasterFilter;
